import {baseApi} from './base'
import type {
  AnomalyDiscussionCommentsInterface,
  AnomalyFeedbackInterface,
  AnomalyWithReasonInterface,
} from '@interfaces/anomaly'
import type {BaseFetchResultsInterface} from '@interfaces/base'

const anomaliesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAnomalies: builder.query<
      BaseFetchResultsInterface<AnomalyWithReasonInterface>,
      {
        productionAreaId?: string
        programId?: string
        machineId?: string
        machineSubSystemId?: string
        offset: number
        limit: number
        status?: string
        anomaly_detection_type?: string
        ordering?: string
      }
    >({
      query: ({
        productionAreaId,
        programId,
        machineId,
        machineSubSystemId,
        offset,
        limit,
        status,
        anomaly_detection_type,
        ordering,
      }) => {
        const queryParameters = []
        if (productionAreaId) {
          queryParameters.push(`batch__machine__production_area=${productionAreaId}`)
        }
        if (programId) {
          queryParameters.push(`batch__program=${programId}`)
        }
        if (machineId) {
          queryParameters.push(`batch__machine=${machineId}`)
        }
        if (machineSubSystemId) {
          queryParameters.push(`batch__program__machine_sub_system=${machineSubSystemId}`)
        }
        if (status) {
          queryParameters.push(`status=${status}`)
        }
        if (anomaly_detection_type) {
          queryParameters.push(`anomaly_detection_type=${anomaly_detection_type}`)
        }
        queryParameters.push(`offset=${offset}`)
        queryParameters.push(`limit=${limit}`)
        if (ordering) {
          queryParameters.push(`ordering=${encodeURIComponent(ordering)}`)
        }

        const queryString = queryParameters.join('&')
        const url = `anomalies/?${queryString}`

        return {url}
      },
      transformResponse: (response: BaseFetchResultsInterface<AnomalyWithReasonInterface>) =>
        response,
      extraOptions: {refetchOnMountOrArgChange: true},
    }),
    getAnomalyById: builder.query<AnomalyWithReasonInterface, string>({
      query: (id) => `anomalies/${id}/`,
    }),
    // Signal Explorer window fetch (AB#5931): anomalies overlapping
    // [tStart, tEnd] for ONE signal — `signal` also reaches step_signal
    // anomalies through the program's step signal, server-side.
    getAnomaliesBySignal: builder.query<
      AnomalyWithReasonInterface[],
      {signalId: string; tStart: string; tEnd: string}
    >({
      query: ({signalId, tStart, tEnd}) =>
        `anomalies/?signal=${signalId}&t_start=${tStart}&t_end=${tEnd}&limit=200`,
      transformResponse: (response: BaseFetchResultsInterface<AnomalyWithReasonInterface>) =>
        response.results,
      keepUnusedDataFor: 300,
    }),
    getAnomalyDiscussionComments: builder.query<
      AnomalyDiscussionCommentsInterface[],
      {anomaly_id: string}
    >({
      query: ({anomaly_id}) => `anomaly-discussion-comments/?anomaly_id=${anomaly_id}`,
      transformResponse: (
        response: BaseFetchResultsInterface<AnomalyDiscussionCommentsInterface>,
      ) => response.results,
    }),
    getAnomalyFeedback: builder.query<AnomalyFeedbackInterface[], {anomaly_id: string}>({
      query: ({anomaly_id}) => `anomaly-feedback/?anomaly_id=${anomaly_id}`,
      transformResponse: (response: BaseFetchResultsInterface<AnomalyFeedbackInterface>) =>
        response.results,
    }),
    createAnomalyDiscussionComment: builder.mutation({
      query: (body) => ({
        url: 'anomaly-discussion-comments/',
        method: 'POST',
        body,
      }),
    }),
    createAnomalyFeedback: builder.mutation({
      query: (body) => ({
        url: 'anomaly-feedback/',
        method: 'POST',
        body,
      }),
    }),
    updateAnomalyDiscussionComment: builder.mutation({
      query: ({id, ...body}) => ({
        url: `anomaly-discussion-comments/${id}/`,
        method: 'PUT',
        body,
      }),
    }),
    deleteAnomalyDiscussionComment: builder.mutation({
      query: (id) => ({
        url: `anomaly-discussion-comments/${id}/`,
        method: 'DELETE',
      }),
    }),
    updateAnomalyFeedback: builder.mutation({
      query: ({id, ...body}) => ({
        url: `anomaly-feedback/${id}/`,
        method: 'PUT',
        body,
      }),
    }),
    deleteAnomalyFeedback: builder.mutation({
      query: (id) => ({
        url: `anomaly-feedback/${id}/`,
        method: 'DELETE',
      }),
    }),
    updateAnomalyLimitedFields: builder.mutation({
      query: ({id, ...body}) => ({
        url: `anomalies/${id}/status-update/`,
        method: 'PATCH',
        body,
      }),
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetAnomaliesQuery,
  useLazyGetAnomaliesQuery,
  useGetAnomalyByIdQuery,
  useGetAnomaliesBySignalQuery,
  useGetAnomalyDiscussionCommentsQuery,
  useGetAnomalyFeedbackQuery,
  useCreateAnomalyDiscussionCommentMutation,
  useCreateAnomalyFeedbackMutation,
  useUpdateAnomalyDiscussionCommentMutation,
  useDeleteAnomalyDiscussionCommentMutation,
  useUpdateAnomalyFeedbackMutation,
  useDeleteAnomalyFeedbackMutation,
  useUpdateAnomalyLimitedFieldsMutation,
} = anomaliesApi
